import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, Crown, Snowflake, UserMinus, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterOption } from '@/types/filters.types';
import {
  getMembershipManagementEnrollments,
  getMembershipManagementOptions,
} from '@/services/memberships-management.service';

import {
  EMPTY_MEMBERSHIP_STATS,
  formatMembershipDateInline,
  MEMBERSHIP_FILTER_CONFIGS,
  MEMBERSHIP_STATUS_OPTIONS,
  membershipStatTiles,
  membershipStatusLabel,
  MEMBERSHIP_STATUS_VARIANTS,
} from './membershipsManagementShared';
import { buildApiParams } from '@/lib/filterRegistry';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

/** Keyed by the tile keys from membershipStatTiles(). */
const STAT_TILE_ICONS: Record<string, JSX.Element> = {
  total: <Crown className="h-4 w-4" />,
  added: <UserPlus className="h-4 w-4" />,
  cancelled: <UserMinus className="h-4 w-4" />,
  frozen: <Snowflake className="h-4 w-4" />,
  declined: <AlertTriangle className="h-4 w-4" />,
};

export default function MembershipsDashboardPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Memberships Dashboard');
  const filters = useLocalDashboardFilters(MEMBERSHIP_FILTER_CONFIGS, 'memberships');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const baseFilters = useMemo(() => ({
    page: 1,
    perPage: 5,
    sortBy: 'createdAt',
    sortDir: 'desc' as const,
    searchType: '0' as const,
  }), []);
  const queryFilters = useMemo(
    () => buildApiParams(effectiveValues, filters.negations, baseFilters),
    [baseFilters, effectiveValues, filters.negations],
  );

  const cancellationFilters = useMemo(() => {
    const withCancelled = { ...effectiveValues, 'membership-status': ['2', '4'] };
    return buildApiParams(withCancelled, filters.negations, baseFilters);
  }, [baseFilters, effectiveValues, filters.negations]);

  const statsQuery = useQuery({
    queryKey: ['memberships-dashboard-enrollments', queryFilters],
    queryFn: () => getMembershipManagementEnrollments(queryFilters),
    staleTime: 60_000,
  });

  const cancellationsQuery = useQuery({
    queryKey: ['memberships-dashboard-cancellations', cancellationFilters],
    queryFn: () => getMembershipManagementEnrollments(cancellationFilters),
    staleTime: 60_000,
  });

  const optionsQuery = useQuery({
    queryKey: ['memberships-dashboard-options'],
    queryFn: getMembershipManagementOptions,
    staleTime: 300_000,
  });

  const stats = statsQuery.data?.stats ?? EMPTY_MEMBERSHIP_STATS;
  const recentEnrollments = statsQuery.data?.items ?? [];
  const recentCancellations = cancellationsQuery.data?.items ?? [];
  const templates = optionsQuery.data?.templates ?? [];
  const facilities = optionsQuery.data?.facilities ?? [];
  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    'membership-type': templates.map((template) => ({ value: String(template.value), label: template.label })),
    'membership-status': MEMBERSHIP_STATUS_OPTIONS,
    facility: facilities.map((facility) => ({ value: String(facility.value), label: facility.label })),
  }), [facilities, templates]);

  // Same five tiles the All Memberships list shows, from the same definition, so
  // the two pages can't drift apart.
  const statTiles = useMemo(() => membershipStatTiles('memberships', stats), [stats]);

  useEffect(() => {
    updateAIContext({
      dataSummary: `Memberships: ${stats.totalCount}, Active: ${stats.activeCount}, Added: ${stats.addedCount}, Cancelled: ${stats.cancelledCount}, Frozen: ${stats.frozenCount}, Past Due: ${stats.declinedCount}, Recent Enrollments: ${recentEnrollments.length}, Recent Cancellations: ${recentCancellations.length}`,
    });
  }, [recentCancellations.length, recentEnrollments.length, stats.activeCount, stats.addedCount, stats.cancelledCount, stats.declinedCount, stats.frozenCount, stats.totalCount, updateAIContext]);

  if (statsQuery.isLoading && !statsQuery.data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" label="Loading memberships..." />
      </div>
    );
  }

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={MEMBERSHIP_FILTER_CONFIGS}
          values={effectiveValues}
          options={filterOptions}
          negations={filters.negations}
          onChange={filters.setFilter}
          onNegateChange={filters.setNegation}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          storageKey="memberships"
          showAIAssistant={false}
        />
      )}
    >
      <div className="space-y-6 overflow-y-auto p-6">
      <PageHeader
        title="Memberships"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => navigate('/memberships/all')}>
              All Memberships
            </Button>
            <Button onClick={() => navigate('/memberships/all?tab=members')}>
              Members
            </Button>
          </div>
        )}
      />

      <MetricCardGrid columns={5}>
        {statTiles.map((tile) => (
          <StatCard
            key={tile.key}
            label={tile.label}
            value={tile.value}
            tone={tile.tone}
            icon={STAT_TILE_ICONS[tile.key]}
            onClick={tile.key === 'total' ? () => navigate('/memberships/all') : undefined}
          />
        ))}
      </MetricCardGrid>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card className="h-full">
          <CardHeader
            action={(
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-primary"
                onClick={() => navigate('/memberships/all')}
              >
                View All <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          >
            <div>
              <CardTitle>Recent Enrollments</CardTitle>
              <p className="mt-1 text-xs text-slate-500">Most recent membership rows from the existing management list.</p>
            </div>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            <div className="divide-y divide-border">
              {statsQuery.isLoading ? (
                <div className="flex items-center justify-center px-5 py-8">
                  <Spinner size="sm" />
                </div>
              ) : recentEnrollments.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-slate-500">No memberships found.</p>
              ) : (
                recentEnrollments.map((row) => (
                  <div key={row.enrollmentId} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">{row.patientName}</p>
                        <p className="mt-1 text-xs text-slate-500">{row.type || 'Membership'} • {formatMembershipDateInline(row.createdAt)}</p>
                      </div>
                      <Badge variant={MEMBERSHIP_STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
                        {membershipStatusLabel(row.status)}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>

        <Card className="h-full">
          <CardHeader
            action={(
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-primary"
                onClick={() => navigate('/memberships/all?status=cancelled')}
              >
                View All <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          >
            <div>
              <CardTitle>Recent Cancellations</CardTitle>
              <p className="mt-1 text-xs text-slate-500">Most recently cancelled memberships.</p>
            </div>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            <div className="divide-y divide-border">
              {cancellationsQuery.isLoading ? (
                <div className="flex items-center justify-center px-5 py-8">
                  <Spinner size="sm" />
                </div>
              ) : recentCancellations.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-slate-500">No cancellations found.</p>
              ) : (
                recentCancellations.map((row) => (
                  <div key={row.enrollmentId} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">{row.patientName}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {row.type || 'Membership'} • Cancelled {formatMembershipDateInline(row.endDate ?? row.createdAt)}
                        </p>
                      </div>
                      <Badge variant={MEMBERSHIP_STATUS_VARIANTS.cancelled} dot>
                        Cancelled
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>
      </div>
      </div>
    </ListPageLayout>
  );
}
